import React from 'react';
import {
  FaCog, FaBell, FaLock, FaGlobe,
  FaToggleOn, FaToggleOff, FaMoon, FaSun, FaShieldAlt,
} from 'react-icons/fa';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import BottomNav from '../components/BottomNav';
import { useApp } from '../context/AppContext';

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'हिन्दी (Hindi)' },
];

const TIMEZONES = [
  { value: 'Asia/Kolkata',       label: 'Asia/Kolkata (IST, UTC+5:30)' },
  { value: 'UTC',                label: 'UTC (Universal Time)' },
  { value: 'Asia/Dubai',         label: 'Asia/Dubai (GST, UTC+4)' },
  { value: 'Asia/Singapore',     label: 'Asia/Singapore (SGT, UTC+8)' },
  { value: 'Asia/Tokyo',         label: 'Asia/Tokyo (JST, UTC+9)' },
  { value: 'Europe/London',      label: 'Europe/London (GMT/BST)' },
  { value: 'Europe/Paris',       label: 'Europe/Paris (CET, UTC+1)' },
  { value: 'America/New_York',   label: 'America/New_York (EST/EDT)' },
  { value: 'America/Chicago',    label: 'America/Chicago (CST/CDT)' },
  { value: 'America/Los_Angeles',label: 'America/Los_Angeles (PST/PDT)' },
];

const Settings = () => {
  const { showToast, settings, updateSetting } = useApp();

  const toggle = (key) => {
    updateSetting(key, !settings[key]);
    showToast('Setting saved', 'success');
  };

  const handleSelect = (key, value) => {
    updateSetting(key, value);
    showToast('Setting saved', 'success');
  };

  const ToggleSwitch = ({ val, onToggle }) => (
    <button
      onClick={onToggle}
      style={{
        background: 'none', border: 'none', cursor: 'pointer',
        fontSize: 28, color: val ? 'var(--primary)' : '#94a3b8',
        display: 'flex', alignItems: 'center', transition: 'color 0.2s',
      }}
    >
      {val ? <FaToggleOn /> : <FaToggleOff />}
    </button>
  );

  const SettingRow = ({ icon: Icon, iconBg, iconColor, label, sub, children }) => (
    <div className="setting-row">
      <div className="setting-icon" style={iconBg ? { background: iconBg } : {}}>
        <Icon style={iconColor ? { color: iconColor } : {}} />
      </div>
      <div className="setting-label">
        <div className="setting-name">{label}</div>
        {sub && <div className="setting-sub">{sub}</div>}
      </div>
      <div style={{ flexShrink: 0 }}>{children}</div>
    </div>
  );

  const SettingSection = ({ title, children }) => (
    <div className="setting-section card">
      <div className="section-title" style={{ marginBottom: 14 }}>{title}</div>
      {children}
    </div>
  );

  const SelectInput = ({ value, onChange, options }) => (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        padding: '8px 12px',
        border: '2px solid var(--border)',
        borderRadius: 8,
        fontSize: 13,
        background: 'var(--primary-bg)',
        color: 'var(--text-dark)',
        cursor: 'pointer',
        maxWidth: 220,
      }}
    >
      {options.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );

  // Preview current time in selected timezone/language
  const previewTime = (() => {
    try {
      const locale = settings.language === 'hi' ? 'hi-IN' : 'en-IN';
      return new Date().toLocaleString(locale, {
        timeZone: settings.timezone,
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        day: '2-digit', month: 'short', year: 'numeric',
      });
    } catch { return '—'; }
  })();

  return (
    <div className="page-wrapper">
      <Header title="Settings" />
      <Sidebar />
      <main className="page-content">
        <div className="page-header">
          <div>
            <h1><FaCog style={{ color: 'var(--primary)', marginRight: 8 }} />Settings</h1>
            <p>App preferences &amp; configuration</p>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* ── Appearance ─────────────────────────────────────────────── */}
          <SettingSection title="Appearance">
            <SettingRow
              icon={settings.darkMode ? FaMoon : FaSun}
              iconBg={settings.darkMode ? '#1e1b4b' : '#fef9c3'}
              iconColor={settings.darkMode ? '#818cf8' : '#ca8a04'}
              label="Dark Mode"
              sub={settings.darkMode ? 'Dark theme is ON' : 'Light theme is ON'}
            >
              <ToggleSwitch val={settings.darkMode} onToggle={() => toggle('darkMode')} />
            </SettingRow>

            <SettingRow
              icon={FaGlobe}
              label="Language"
              sub={LANGUAGES.find(l => l.value === settings.language)?.label || 'English'}
            >
              <SelectInput
                value={settings.language}
                onChange={v => handleSelect('language', v)}
                options={LANGUAGES}
              />
            </SettingRow>

            <SettingRow
              icon={FaGlobe}
              label="Timezone"
              sub="Affects how dates and times are displayed"
            >
              <SelectInput
                value={settings.timezone}
                onChange={v => handleSelect('timezone', v)}
                options={TIMEZONES}
              />
            </SettingRow>

            {/* Live preview */}
            <div style={{
              marginTop: 10, padding: '10px 14px',
              background: 'var(--primary-pale)', borderRadius: 10,
              fontSize: 12, color: 'var(--text-medium)',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <FaGlobe style={{ color: 'var(--primary)', flexShrink: 0 }} />
              <span>Current time in <strong>{settings.timezone}</strong>: {previewTime}</span>
            </div>
          </SettingSection>

          {/* ── Notifications ──────────────────────────────────────────── */}
          <SettingSection title="Notifications">
            <SettingRow icon={FaBell} label="Push Notifications" sub="In-app alerts and reminders">
              <ToggleSwitch val={settings.notifications} onToggle={() => toggle('notifications')} />
            </SettingRow>
            <SettingRow icon={FaBell} label="Email Alerts" sub="Receive alerts via email">
              <ToggleSwitch val={settings.emailAlerts} onToggle={() => toggle('emailAlerts')} />
            </SettingRow>
            <SettingRow icon={FaBell} label="SMS Alerts" sub="Text message notifications">
              <ToggleSwitch val={settings.smsAlerts} onToggle={() => toggle('smsAlerts')} />
            </SettingRow>
            <SettingRow icon={FaBell} label="Appointment Reminders" sub="Remind before upcoming appointments">
              <ToggleSwitch val={settings.appointmentReminder} onToggle={() => toggle('appointmentReminder')} />
            </SettingRow>
          </SettingSection>

          {/* ── Security ───────────────────────────────────────────────── */}
          <SettingSection title="Security">
            <SettingRow
              icon={FaShieldAlt}
              iconBg="#f0fdf4"
              iconColor="#16a34a"
              label="Two-Factor Authentication"
              sub="Extra security layer on login"
            >
              <ToggleSwitch val={settings.twoFactor} onToggle={() => toggle('twoFactor')} />
            </SettingRow>
            <SettingRow icon={FaLock} label="Change Password" sub="Update your account password">
              <button
                className="btn btn-outline"
                style={{ fontSize: 12, padding: '7px 14px' }}
                onClick={() => showToast('Change password from the Profile page', 'success')}
              >
                Go to Profile
              </button>
            </SettingRow>
          </SettingSection>

          {/* ── About ──────────────────────────────────────────────────── */}
          <div className="card" style={{ textAlign: 'center', padding: 20 }}>
            <div style={{ fontSize: 12, color: 'var(--text-light)', marginBottom: 4 }}>DocApps Solutions Pvt Ltd</div>
            <div style={{ fontSize: 11, color: 'var(--text-light)' }}>Version 3.0.0 &bull; &copy; 2026</div>
          </div>
        </div>
      </main>
      <BottomNav />

      <style>{`
        .setting-section { padding: 16px; }
        .setting-row {
          display: flex; align-items: center; gap: 12px;
          padding: 12px 0; border-bottom: 1px solid var(--border);
        }
        .setting-row:last-child { border-bottom: none; }
        .setting-icon {
          width: 38px; height: 38px; background: var(--primary-bg);
          border-radius: 10px; display: flex; align-items: center;
          justify-content: center; color: var(--primary); font-size: 16px; flex-shrink: 0;
        }
        .setting-label { flex: 1; min-width: 0; }
        .setting-name { font-size: 14px; font-weight: 600; color: var(--text-dark); }
        .setting-sub { font-size: 12px; color: var(--text-light); margin-top: 2px; }
      `}</style>
    </div>
  );
};

export default Settings;
